import { isNativePlatform } from "@/utils/platform";

let weixinSdkPromise = null;

function loadWeixinSdk() {
    if (typeof window === "undefined" || typeof document === "undefined") return Promise.resolve(null);
    if (globalThis.wx) return Promise.resolve(globalThis.wx);
    if (weixinSdkPromise) return weixinSdkPromise;

    weixinSdkPromise = new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => resolve(globalThis.wx || null), 3000);
        const finish = (handler, value) => {
            window.clearTimeout(timer);
            handler(value);
        };
        const existed = document.querySelector('script[data-miniprogram-jssdk="true"]');
        if (existed) {
            existed.addEventListener("load", () => finish(resolve, globalThis.wx || null), { once: true });
            existed.addEventListener("error", (error) => finish(reject, error), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
        script.async = true;
        script.dataset.miniprogramJssdk = "true";
        script.onload = () => finish(resolve, globalThis.wx || null);
        script.onerror = (error) => finish(reject, error);
        document.head.appendChild(script);
    });

    return weixinSdkPromise;
}

export function getMiniProgramBridge() {
    return globalThis.wx?.miniProgram || null;
}

function normalize(value = "") {
    return String(value || "")
        .trim()
        .toLowerCase();
}

function getHashQuery(hash = "") {
    const queryStart = String(hash || "").indexOf("?");
    if (queryStart === -1) return "";

    const query = String(hash || "").slice(queryStart + 1);
    const hashStart = query.indexOf("#");
    return hashStart === -1 ? query : query.slice(0, hashStart);
}

function hasMiniProgramSource(search = "", hash = "") {
    const searchParams = new URLSearchParams(search || "");
    if (normalize(searchParams.get("from")) === "miniprogram") return true;
    if (searchParams.has("mpNav") || searchParams.has("mpStatus") || searchParams.has("mpMenuTop")) return true;

    const hashParams = new URLSearchParams(getHashQuery(hash));
    return normalize(hashParams.get("from")) === "miniprogram";
}

function hasMiniProgramUserAgent(userAgent = "") {
    return normalize(userAgent).includes("miniprogram");
}

export function isMiniProgramWebview(options = {}) {
    if (typeof window === "undefined") return false;

    const search = options.search ?? window.location?.search ?? "";
    const hash = options.hash ?? window.location?.hash ?? "";
    const userAgent = options.userAgent ?? globalThis.navigator?.userAgent ?? "";
    const wxjsEnvironment = normalize(globalThis.window?.__wxjs_environment || "");

    return (
        wxjsEnvironment === "miniprogram" || hasMiniProgramSource(search, hash) || hasMiniProgramUserAgent(userAgent)
    );
}

export function isMiniProgramWebviewShell(options = {}) {
    return !isNativePlatform() && isMiniProgramWebview(options);
}

function stringifyQuery(params = {}) {
    return Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
}

function normalizeRoutePath(path = "/") {
    const text = String(path || "/").trim();
    if (!text || /^(?:javascript:|void\b)/i.test(text)) return "/";
    if (text.startsWith("/#/")) return normalizeRoutePath(text.slice(2));
    if (text.startsWith("#/")) return text.slice(1);
    return text.startsWith("/") ? text : `/${text}`;
}

function normalizeExternalMiniProgramTarget(target = {}) {
    const path = String(target.path || "").trim();
    return {
        appId: String(target.appId || "").trim(),
        path: path.replace(/^\/+/, ""),
        envVersion: target.envVersion || "release",
        extraData: target.extraData || {},
    };
}

function invokeMiniProgramNavigateTo(bridge, url) {
    return new Promise((resolve) => {
        if (!bridge?.navigateTo) {
            resolve(false);
            return;
        }

        let settled = false;
        let timer = null;
        const timerApi = typeof window !== "undefined" ? window : globalThis;
        const finish = (opened) => {
            if (settled) return;
            settled = true;
            if (timer) timerApi.clearTimeout(timer);
            resolve(opened);
        };

        try {
            bridge.navigateTo({
                url,
                success: () => finish(true),
                fail: (error) => {
                    console.warn("[miniprogram-bridge] wx.miniProgram.navigateTo failed", error);
                    finish(false);
                },
            });

            if (!settled) {
                timer = timerApi.setTimeout(() => finish(true), 1500);
            }
        } catch (error) {
            console.warn("[miniprogram-bridge] wx.miniProgram.navigateTo failed", error);
            finish(false);
        }
    });
}

export function postMiniProgramMessage(message) {
    const bridge = getMiniProgramBridge();
    if (bridge?.postMessage) {
        bridge.postMessage({ data: message });
        return;
    }

    loadWeixinSdk()
        .then(() => getMiniProgramBridge()?.postMessage?.({ data: message }))
        .catch((error) => {
            console.warn("[miniprogram-bridge] postMessage failed", error);
        });
}

export function buildMiniProgramWebviewUrl(path = "/", query = {}) {
    const routePath = normalizeRoutePath(path);
    const webviewQuery = stringifyQuery({
        path: routePath,
        query: Object.keys(query || {}).length ? JSON.stringify(query) : "",
    });

    return webviewQuery ? `/pages/webview/webview?${webviewQuery}` : "/pages/webview/webview";
}

export function buildMiniProgramLaunchUrl(target = {}) {
    const normalizedTarget = normalizeExternalMiniProgramTarget(target);
    const query = stringifyQuery({
        target: JSON.stringify(normalizedTarget),
    });

    return `/pages/mini-program-launch/index?${query}`;
}

export async function navigateMiniProgramWebviewPath(path = "/", query = {}) {
    if (!isMiniProgramWebviewShell()) {
        return false;
    }

    const url = buildMiniProgramWebviewUrl(path, query);
    const bridge = getMiniProgramBridge();
    if (bridge?.navigateTo) {
        return invokeMiniProgramNavigateTo(bridge, url);
    }

    try {
        await loadWeixinSdk();
        return invokeMiniProgramNavigateTo(getMiniProgramBridge(), url);
    } catch (error) {
        console.warn("[miniprogram-bridge] navigateTo webview failed", error);
        return false;
    }
}

export function navigateMiniProgram(url) {
    const bridge = getMiniProgramBridge();
    if (bridge?.navigateTo) {
        bridge.navigateTo({ url });
        return true;
    }

    loadWeixinSdk()
        .then(() => {
            const loadedBridge = getMiniProgramBridge();
            if (!loadedBridge?.navigateTo) {
                console.warn("[miniprogram-bridge] wx.miniProgram.navigateTo unavailable", url);
                return;
            }
            loadedBridge.navigateTo({ url });
        })
        .catch((error) => {
            console.warn("[miniprogram-bridge] navigateTo failed", error);
        });
    return false;
}

export async function openExternalMiniProgram(target = {}) {
    const normalizedTarget = normalizeExternalMiniProgramTarget(target);
    if (!normalizedTarget.appId) return false;

    const url = buildMiniProgramLaunchUrl(normalizedTarget);
    const bridge = getMiniProgramBridge();
    if (bridge?.navigateTo) {
        return invokeMiniProgramNavigateTo(bridge, url);
    }

    try {
        await loadWeixinSdk();
        const loadedBridge = getMiniProgramBridge();
        if (loadedBridge?.navigateTo) {
            return invokeMiniProgramNavigateTo(loadedBridge, url);
        }
    } catch (error) {
        console.warn("[miniprogram-bridge] launch external mini program failed", error);
    }

    postMiniProgramMessage({
        type: "navigateToMiniProgram",
        payload: normalizedTarget,
    });
    return false;
}

export function reLaunchMiniProgram(url) {
    const bridge = getMiniProgramBridge();
    if (bridge?.reLaunch) {
        bridge.reLaunch({ url });
        return;
    }
    if (bridge?.navigateTo) {
        bridge.navigateTo({ url });
        return;
    }

    loadWeixinSdk()
        .then(() => {
            const loadedBridge = getMiniProgramBridge();
            if (loadedBridge?.reLaunch) {
                loadedBridge.reLaunch({ url });
                return;
            }
            loadedBridge?.navigateTo?.({ url });
        })
        .catch((error) => {
            console.warn("[miniprogram-bridge] reLaunch failed", error);
        });
}

export function navigateMiniProgramLogout() {
    postMiniProgramMessage({ type: "logout" });
    reLaunchMiniProgram("/pages/index/index?login=1");
}
